<template>
  <div class="securone sales-dashboard min-h-screen">
    <LoadingSpinner :isLoading="loading" />

    <!-- absent, not disabled: when the add-on is not present the surface does not exist -->
    <div v-if="!loading && !enabled" class="w-full px-4 sm:px-6 lg:px-8 py-6">
      <SoNote v-if="errorId === 'role_not_allowed'" tone="info" icon="lock">{{ $t("securone.context.not_your_feature") }}</SoNote>
      <SoNote v-else-if="isAdminRole" tone="info" icon="building">
        {{ $t("securone.context.admin_choose_customer") }}
        <router-link to="/admin/kunder" class="link ml-1">{{ $t("secure_cloud_menu.customer") }}</router-link>
      </SoNote>
      <SoNote v-else tone="info" icon="lock">{{ $t("securone.context.not_available") }}</SoNote>
    </div>

    <template v-else-if="!loading">
      <!-- Header: same shell as the agreements / commissions / sales views -->
      <!-- Header: logo, title and customer on one row; underline tabs below (like the other SecurCloud views) -->
      <div class="sd-header">
        <div class="w-full px-4 sm:px-6 lg:px-8 pt-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <div class="sd-header__logo"><BaseIcon icon="shield-alt" class="text-white" /></div>
              <div>
                <h1 class="text-xl font-semibold text-secondary-800">SecurOne</h1>
                <p class="text-sm text-gray-500">{{ customer.name }}<template v-if="departments.length"> · {{ departments.length === 1 ? departments[0].name : $tc("securone.context.departments_count", departments.length, { n: departments.length }) }}</template></p>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <div v-if="organisations.length > 1" class="flex items-center gap-2">
                <label class="text-sm text-gray-600">{{ $t("securone.context.customer") }}:</label>
                <select :value="customer._id" @change="switchOrganisation($event.target.value)" class="sd-select sd-select--sm">
                  <option v-for="o in organisations" :key="o._id" :value="o._id">{{ o.name }}</option>
                </select>
              </div>
              <!-- desktop notifications for broadcasts: ask once, then a mute toggle -->
              <button v-if="notificationPermission === 'default'" class="sd-btn sd-btn--secondary sd-btn--sm" @click="enableNotifications"><BaseIcon icon="bell" class="mr-1" />{{ $t("securone.notifications.enable") }}</button>
              <span v-else-if="notificationPermission === 'denied'" class="text-xs text-secondary-500" :title="$t('securone.notifications.denied_help')"><BaseIcon icon="bell-slash" class="mr-1" />{{ $t("securone.notifications.denied") }}</span>
              <button v-if="isPrincipal" class="sd-btn sd-btn--secondary sd-btn--sm" :title="soundOn ? $t('securone.notifications.sound_on') : $t('securone.notifications.sound_off')" @click="toggleSound"><BaseIcon :icon="soundOn ? 'volume-up' : 'volume-mute'" /></button>
            </div>
          </div>
          <nav class="so-tabs mt-3">
            <router-link v-for="item in visibleNav" :key="item.name" :to="{ name: item.name }" class="so-tabs__tab" :class="{ 'so-tabs__tab--active': isActive(item) }">
              <BaseIcon :icon="item.icon" class="so-tabs__icon" />{{ $t("securone.nav." + item.label) }}
            </router-link>
          </nav>
        </div>
      </div>

      <div class="w-full px-4 sm:px-6 lg:px-8 py-6">
        <router-view :key="customer._id" />
      </div>
    </template>
  </div>
</template>

<script>
import SoNote from "./components/SoNote.vue";
import * as socket from "./securone.socket";
import * as notifications from "./securone.notifications";
import { NAV } from "./securone.nav";

// Shell of the SecurOne administration view: loads the context (the add-on
// gate - who am I here, what may I write), connects the browser socket for
// list refreshes and hosts the screens. Entry from the customer / verksamhet
// view: /securone?org=<customerId>&unit=<unitId>. Visual language: the
// sales-dashboard design system (sd-*), like the agreements and commissions views.
export default {
  name: "SecurOne",
  title() {
    return "SecurOne";
  },
  components: { SoNote },
  data() {
    return {
      nav: NAV,
      notificationPermission: notifications.permission(),
      soundOn: notifications.soundEnabled(),
    };
  },
  computed: {
    loading() {
      return this.$store.state.securone.loading;
    },
    errorId() {
      return this.$store.state.securone.error;
    },
    isAdminRole() {
      return this.$store.state.user && this.$store.state.user.role === "admin";
    },
    enabled() {
      return this.$store.getters["securone/enabled"];
    },
    kind() {
      return this.$store.getters["securone/kind"];
    },
    canAdminister() {
      return this.$store.getters["securone/canAdminister"];
    },
    customer() {
      return this.$store.getters["securone/customer"];
    },
    organisations() {
      return this.$store.getters["securone/organisations"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    isPrincipal() {
      return this.$store.getters["securone/isPrincipal"];
    },
    canRaiseFreeText() {
      return this.$store.getters["securone/canRaiseFreeText"];
    },
    visibleNav() {
      return this.nav.filter((item) => !item.when || this[item.when]);
    },
  },
  methods: {
    async enableNotifications() {
      notifications.primeSound();
      this.notificationPermission = await notifications.requestPermission();
      if (this.notificationPermission === "granted") notifications.start();
    },
    toggleSound() {
      this.soundOn = !this.soundOn;
      notifications.setSoundEnabled(this.soundOn);
      if (this.soundOn) notifications.primeSound();
    },
    isActive(item) {
      return item.match.includes(this.$route.name);
    },
    async load(customerId = null, unitId = null) {
      const context = await this.$store.dispatch("securone/loadContext", { customerId, unitId });
      if (context && context.addonEnabled) {
        // Remember the customer across reloads: an admin arrives from a
        // customer's unit view with ?org= and must keep it on the sub-routes.
        try {
          localStorage.setItem("securone_org", context.customer._id);
        } catch (e) {
          /* storage unavailable */
        }
        // arriving from a verksamhet pre-selects it as the list filter
        if (unitId && context.departments.some((d) => String(d._id) === String(unitId))) this.$store.commit("securone/SET_DEPARTMENT_FILTER", String(unitId));
        socket.connect();
        socket.joinOrganisation(context.customer._id);
        notifications.start();
      }
    },
    rememberedOrganisation() {
      try {
        return localStorage.getItem("securone_org");
      } catch (e) {
        return null;
      }
    },
    async switchOrganisation(customerId) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", null);
      await this.load(customerId);
      if (this.$route.name !== "securone_overview") this.$router.push({ name: "securone_overview" });
    },
  },
  async created() {
    this.setPageTitle("SecurOne", this.$route.name);
    await this.load(this.$route.query.org || this.rememberedOrganisation() || null, this.$route.query.unit || null);
  },
  beforeDestroy() {
    // the socket stays up: securone.notifications keeps listening for broadcasts elsewhere in SecurCloud
    this.$store.dispatch("securone/reset");
  },
};
</script>
